/**
 * What a proposed break or vacation would clash with.
 *
 * *** THE PREVIEW CHANGES NOTHING, AND NEITHER DOES SAVING ***
 * Adding availability never voids a settled booking (ARCHITECTURE §6.3,
 * Race 3). So this dialog answers two questions in order, and never
 * conflates them:
 *
 *   1. Shall I block this time?  — always allowed
 *   2. Shall I cancel the sessions already in it? — a separate decision
 *
 * Cancelling a paid session must not ride along on the same tap. The app
 * makes the same split, and for the same reason.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useAffectedBookings, useCancelAffectedBookings } from '@/lib/queries'
import { formatDateTime } from '@/lib/format'
import styles from './AvailabilityPage.module.css'

export interface ProposedRange {
  start_date: string
  end_date: string
  start_time?: string
  end_time?: string
}

export function AffectedBookingsDialog({
  range,
  what,
  onConfirm,
  onClose,
  saving,
}: {
  range: ProposedRange
  /** "break" or "time off" — used in the sentences. */
  what: string
  /** Block the time. Cancelling, if any, has already happened. */
  onConfirm: () => Promise<void>
  onClose: () => void
  saving: boolean
}) {
  const toast = useToast()
  const preview = useAffectedBookings(range)
  const cancel = useCancelAffectedBookings()

  const [cancelled, setCancelled] = useState(false)

  const data = preview.data
  const clashes = data?.bookings ?? []

  const cancelAll = async () => {
    try {
      const result = await cancel.mutateAsync(clashes.map((b) => b.booking_id))

      /*
        *** PARTIAL FAILURE IS REPORTED, NOT SWALLOWED ***
        The tenant's policy can refuse an individual cancellation — too
        close to the start, usually. The server reports per booking rather
        than stopping at the first, so a mentor who is told "done" while
        two sessions survive would walk into them.
      */
      if (result.failed.length > 0) {
        toast.error(
          `${result.cancelled.length} cancelled, ${result.failed.length} could not be: ${result.failed[0]}`,
        )
      } else {
        toast.success(
          `${result.cancelled.length} session${result.cancelled.length === 1 ? '' : 's'} cancelled.`,
        )
      }
      setCancelled(true)
      await preview.refetch()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not cancel those sessions.',
      )
    }
  }

  return (
    <div className={styles.backdrop} role="dialog" aria-modal="true">
      <div className={styles.dialog}>
        <h2 className={styles.dialogTitle}>Add this {what}?</h2>

        {preview.isLoading && (
          <p className={styles.muted}>Checking your sessions…</p>
        )}

        {preview.error && (
          <p className={styles.error}>
            {preview.error instanceof ApiError
              ? preview.error.message
              : 'Could not check for clashes.'}
          </p>
        )}

        {data && clashes.length === 0 && (
          <p className={styles.dialogText}>
            {cancelled
              ? 'Those sessions are cancelled. Nothing else clashes.'
              : 'Nothing is booked in that time.'}
          </p>
        )}

        {clashes.length > 0 && (
          <>
            <p className={styles.dialogText}>
              {clashes.length === 1
                ? 'One session is already booked in that time.'
                : `${clashes.length} sessions are already booked in that time.`}{' '}
              {/*
                The honest statement of what saving does. A mentor who
                assumes the break clears their diary would not turn up.
              */}
              Adding the {what} does <strong>not</strong> cancel them.
            </p>

            <ul className={styles.clashes}>
              {clashes.map((b) => (
                <li key={b.booking_id} className={styles.clash}>
                  <span className={styles.clashWhen}>
                    {formatDateTime(b.slot_start_utc)}
                  </span>
                  <span className={styles.clashWho}>{b.student_name}</span>
                </li>
              ))}
            </ul>

            {/*
              *** THE TENANT MAY FORBID THIS ENTIRELY ***
              Offering a button the server will refuse is the failure to
              avoid; the reason is given instead.
            */}
            {data?.mentor_can_cancel ? (
              <Button
                variant="danger"
                onClick={cancelAll}
                loading={cancel.isPending}
              >
                Cancel {clashes.length === 1 ? 'this session' : 'these sessions'}
              </Button>
            ) : (
              <p className={styles.warnNote}>
                Your institute does not allow mentors to cancel sessions.
                Ask them to move these.
              </p>
            )}
          </>
        )}

        <div className={styles.dialogActions}>
          <Button variant="ghost" onClick={onClose}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={saving}
            /* Blocking the time is always allowed — the clash is the
               mentor's to judge, and the preview has told them. */
            disabled={preview.isLoading}
          >
            Add the {what}
          </Button>
        </div>
      </div>
    </div>
  )
}
